import { API_CONFIG } from '../config/api';
import type { PhysicalStockCount, CreatePhysicalStockCountRequest, PhysicalStockCountsResponse, DetailedPhysicalStockCount } from '../types/physicalStockCount';

/**
 * Create a new physical stock count
 */
export const createPhysicalStockCount = async (data: CreatePhysicalStockCountRequest): Promise<PhysicalStockCount> => {
  try {
    console.log('Creating physical stock count with data:', data);

    const response = await fetch(`${API_CONFIG.BASE_URL}/physical-stock-counts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Physical stock count creation failed:', response.status, errorText);
      
      let errorMessage = 'Failed to create physical stock count';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Physical stock count created successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error creating physical stock count:', error);
    throw error;
  }
};

/**
 * Update an existing physical stock count
 */
export const updatePhysicalStockCount = async (
  countId: number, 
  items: Array<{ id: number; counted_quantity: number }>
): Promise<PhysicalStockCount> => {
  try {
    console.log('Updating physical stock count with ID:', countId, 'and items:', items);

    const response = await fetch(`${API_CONFIG.BASE_URL}/physical-stock-counts/${countId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Physical stock count update failed:', response.status, errorText);
      
      let errorMessage = 'Failed to update physical stock count';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Physical stock count updated successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error updating physical stock count:', error);
    throw error;
  }
};

/**
 * Get a physical stock count by ID
 */
export const getPhysicalStockCount = async (countId: number): Promise<DetailedPhysicalStockCount> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/physical-stock-counts/${countId}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch physical stock count:', response.status, errorText);
      throw new Error(`Failed to fetch physical stock count: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching physical stock count:', error);
    throw new Error('Failed to fetch physical stock count');
  }
};

/**
 * Get all physical stock counts
 */
export const getAllPhysicalStockCounts = async (): Promise<PhysicalStockCountsResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/physical-stock-counts`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch physical stock counts:', response.status, errorText);
      throw new Error(`Failed to fetch physical stock counts: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching physical stock counts:', error);
    throw new Error('Failed to fetch physical stock counts');
  }
};

/**
 * Delete a physical stock count
 */
export const deletePhysicalStockCount = async (countId: number): Promise<void> => {
  try {
    console.log('Deleting physical stock count with ID:', countId);

    const response = await fetch(`${API_CONFIG.BASE_URL}/physical-stock-counts/${countId}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Physical stock count deletion failed:', response.status, errorText);
      
      let errorMessage = 'Failed to delete physical stock count';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Physical stock count deleted successfully');
  } catch (error) {
    console.error('Error deleting physical stock count:', error);
    throw error;
  }
};
